import React, {useEffect, useRef, useState} from "react";
import {AnimatePresence, motion} from "framer-motion";
import cn from "classnames";
import {isMobile} from "react-device-detect";
import {clsx} from "clsx";
import {isEmpty} from "lodash";
import IMG from "../../../constants/image";
import {FiChevronDown} from "react-icons/fi";
import {removeDiacritics} from "../../../utils";

const SelectComponent = ({
                             title,
                             name,
                             viewOnly,
                             required,
                             placeholder,
                             onChange,
                             error,
                             data,
                             value,
                             isAddress,
                             isOrderConfirm
                         }) => {

    // State Management
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);
    const [filteredData, setFilteredData] = useState(data);
    const [searchFilter, setSearchFilter] = useState('');
    const selectRef = useRef(null);

    // Helper Functions
    const filterOptions = (list, searchText) => {
        return (list || []).filter((option) =>
            removeDiacritics(option?.name?.toLowerCase()).includes(
                removeDiacritics(searchText.toLowerCase())
            )
        );
    };

    // Event Handlers
    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        setSearchFilter(value);
        setIsOpen(true);

        if (!value) {
            onChange && onChange({target: {name, value: "", code: ""}});
        }
    };

    const toggleSelect = (e) => {
        e.preventDefault();
        setSearchFilter('');
        setIsOpen(!isOpen);
    };

    const handleOptionSelect = (option) => {
        setSelectedOption(option);
        setInputValue(option.name);
        onChange && onChange({target: {name, value: option.id, code: option.code}});
        setIsOpen(false);
        setSearchFilter('');
    };

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectRef.current && !selectRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Reset filtered data when data changes
    useEffect(() => {
        setFilteredData(data);
        setSearchFilter('');
    }, [data]);

    // Filter data when search filter changes
    useEffect(() => {
        setFilteredData(filterOptions(data, searchFilter));
    }, [searchFilter, data]);

    // Set selected option based on value
    useEffect(() => {
        if (isEmpty(data)) return;

        const compareValue = value != null ? String(value).toLowerCase() : '';

        const selectedOption = data.find((option) => {
            const optionId = option.id != null ? String(option.id).toLowerCase() : '';
            const optionFullName = option?.fullName != null ? String(option?.fullName).toLowerCase() : '';

            return optionId === compareValue || optionFullName === compareValue || '';
        });

        setSelectedOption(selectedOption);
        setInputValue(selectedOption?.name || "");
    }, [data, value]);


    // View Only Mode
    if (viewOnly) {
        return (
            <div className='text-sm w-full'>
                {title && <p className='mb-1'>{title}</p>}
                <p className='text-[#9E9E9E] py-2'>{value}</p>
            </div>
        );
    }

    // Title Component
    const TitleComponent = () => (
        <div className='flex items-center space-x-1'>
            {isOrderConfirm && isMobile ? '' : <div className='text-sm'>{title}</div>}
            {required && title && !(isOrderConfirm && isMobile) && (
                <div className='text-[#E4626F]'>*</div>
            )}
        </div>
    );

    // Error Message Component
    const ErrorMessage = () => (
        <>
            {error && <p className="text-[#E4626F] text-xs mt-1">{error}</p>}
        </>
    );

    // Options Dropdown Component
    const OptionsDropdown = () => (
        <motion.div
            initial={{opacity: 0, y: isMobile && !isOrderConfirm ? "100%" : 0}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: isMobile && !isOrderConfirm ? "100%" : 0}}
            transition={{duration: 0.3}}
            className={clsx(
                `fixed md:absolute z-10 overflow-y-scroll bg-white rounded-xl shadow-lg`,
                (isMobile && !isOrderConfirm) ? "fixed h-[50%] top-[50%] left-0 right-0" : "mt-1",
                isAddress ? 'md:max-h-[200px]' : 'md:max-h-[300px]',
                (isOrderConfirm && isMobile) ? 'min-w-[398px] max-h-[200px]' : 'w-full md:min-w-[200px]'
            )}
        >
            <div>
                <div className={clsx(
                    'text-sm text-center font-bold border-b border-[#DEDEDE]',
                    isMobile && isOrderConfirm ? 'p-2' : 'p-4'
                )}>
                    {`Chọn ${title}`}
                </div>
                <div
                    className={clsx(
                        'md:hidden absolute right-4',
                        isMobile && isOrderConfirm ? 'top-2' : 'top-4'
                    )}
                    onClick={() => setIsOpen(false)}
                >
                    <img className='w-5 h-5' src={IMG.icon.close} alt="close"/>
                </div>
                {(filteredData || []).map((option) => (
                    <button
                        key={option.id}
                        onClick={(e) => {
                            e.preventDefault();
                            handleOptionSelect(option);
                        }}
                        className={clsx(
                            "flex text-xs border-b border-[#DEDEDE] w-full px-4 hover:bg-gray-100 gap-1 items-center",
                            isAddress ? 'py-2' : 'py-3'
                        )}
                        role="option"
                        aria-selected={(selectedOption?.id || '').toLowerCase() === (option.id || '').toLowerCase()}
                    >
                        <img src={IMG.icon.location} alt="location"/>
                        <p className='text-left'>{option.name}</p>
                    </button>
                ))}
            </div>
        </motion.div>
    );

    // Mobile Overlay Component
    const MobileOverlay = () => (
        <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.2}}
            className="fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={() => setIsOpen(false)}
        />
    );

    // Main Render
    return (
        <div className="relative w-full" ref={selectRef}>
            <TitleComponent/>
            {isAddress ?
                <div className='relative'>
                    <input
                        value={inputValue}
                        onChange={handleInputChange}
                        onFocus={() => setIsOpen(true)}
                        placeholder={placeholder}
                        aria-haspopup="listbox"
                        aria-expanded={isOpen}
                        className={cn(
                            "flex text-sm cursor-pointer items-center justify-between w-full px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500",
                            error && "border-red-500"
                        )}
                    />
                    <FiChevronDown
                        onClick={() => setIsOpen(!isOpen)}
                        className={clsx(
                            'w-5 h-5 absolute right-3 top-1/4 transition-transform duration-300',
                            isOpen ? "transform rotate-180" : "",
                        )}
                    />
                </div> :
                <button
                    onClick={toggleSelect}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    className={cn(
                        "flex text-sm items-center justify-between w-full px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500",
                        error && "border-red-500"
                    )}
                >
                    <span className={cn(
                        'whitespace-nowrap overflow-hidden text-ellipsis',
                        selectedOption ? '' : 'text-[#D9D9D9]'
                    )}>{selectedOption ? selectedOption.name : placeholder}</span>
                    <FiChevronDown
                        className={clsx(
                            'w-5 h-5',
                            isOpen ? "transform rotate-180" : "",
                            (isMobile && isOrderConfirm) ? 'top-1/4' : 'top-1/2',
                            'absolute right-3',
                            'transition-transform ease-in-out duration-200'
                        )}/>
                </button>
            }
            <ErrorMessage/>
            <AnimatePresence>
                {isOpen && (
                    <>
                        {isMobile && !isOrderConfirm && <MobileOverlay/>}
                        <OptionsDropdown/>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SelectComponent;